import { z } from "zod";

/** Shared primitives used by several schema modules. Keep this file dependency-free. */

export function isValidTimeZone(tz: string): boolean {
  try {
    new Intl.DateTimeFormat("en-US", { timeZone: tz });
    return true;
  } catch {
    return false;
  }
}

export const timeZoneSchema = z
  .string()
  .min(1)
  .refine(isValidTimeZone, { message: "Unknown time zone" });

/** Two-letter US state (used for recording-consent rules, RCP-15). */
export const usStateSchema = z.string().regex(/^[A-Z]{2}$/, "Use the two-letter state code");

export const addressSchema = z.object({
  line1: z.string().min(1).max(200),
  line2: z.string().max(200).optional(),
  city: z.string().min(1).max(100),
  state: usStateSchema,
  postalCode: z.string().min(3).max(12),
  country: z.string().length(2).default("US"),
});
export type Address = z.infer<typeof addressSchema>;

export const latLngSchema = z.object({
  lat: z.number().min(-90).max(90),
  lng: z.number().min(-180).max(180),
});
export type LatLng = z.infer<typeof latLngSchema>;

/** Calendar date in the tenant's time zone, YYYY-MM-DD. */
export const localDateSchema = z.string().regex(/^\d{4}-\d{2}-\d{2}$/, "Use YYYY-MM-DD");
export type LocalDate = z.infer<typeof localDateSchema>;

/** Minutes after local midnight, 0..1440 (1440 allowed as an exclusive end). */
export const minutesOfDaySchema = z.number().int().min(0).max(1440);

/** 0 = Sunday ... 6 = Saturday (JavaScript convention). */
export const weekdaySchema = z.number().int().min(0).max(6);
export type Weekday = 0 | 1 | 2 | 3 | 4 | 5 | 6;

export const hoursRangeSchema = z
  .object({
    weekday: weekdaySchema,
    startMin: minutesOfDaySchema,
    endMin: minutesOfDaySchema,
  })
  .refine((r) => r.startMin < r.endMin, { message: "End must be after start" });
export type HoursRange = z.infer<typeof hoursRangeSchema>;

export const weeklyHoursSchema = z.array(hoursRangeSchema).max(28);
export type WeeklyHours = z.infer<typeof weeklyHoursSchema>;

/**
 * A true/false query-string flag. Not `z.coerce.boolean()`: that turns the text "false" into
 * true. Accepts true/false as booleans or the strings "true"/"false"/"1"/"0".
 */
export const queryFlagSchema = z.preprocess(
  (v) => (v === "true" || v === "1" ? true : v === "false" || v === "0" ? false : v),
  z.boolean(),
);
